import { gql, useQuery } from '@apollo/client';
import { Box, Image, ImageProps, Stack } from '@chakra-ui/react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import {
  DynamicImageInput,
  EPrepImagesSource,
} from '@/graphql-types/globalTypes';
import { useImageUpload } from '@/hooks/useImageUpload';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';

import {
  DragAndDropImageUpload_GetDraftImageProxy,
  DragAndDropImageUpload_GetDraftImageProxyVariables,
} from './graphql-types/DragAndDropImageUpload_GetDraftImageProxy';

import { DragAndDropImageUploader } from './components/DragAndDropUploader';

const GET_DRAFT_IMAGE_PROXY = gql`
  query DragAndDropImageUpload_GetDraftImageProxy(
    $getDraftImageProxyInput: GetDraftImageProxyInput!
    $dynamicImageInput: DynamicImageInput!
  ) {
    getDraftImageProxy(input: $getDraftImageProxyInput) {
      imageId
      imageProxy(input: $dynamicImageInput)
    }
  }
`;

export const DragAndDropImageUpload: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  source: EPrepImagesSource;
  imageInput: DynamicImageInput;
  isDisabled?: boolean;
  imageProps?: ImageProps;
  nonDraftImageProxy: string | null;
  newView?: boolean;
  imageSizeSmall?: boolean;
  imageSizeLarge?: boolean; 
}> = ({
  value,
  onChange,
  source,
  imageInput,
  isDisabled,
  imageProps,
  nonDraftImageProxy,
  newView= false,
  imageSizeSmall = false,
  imageSizeLarge = false,
}) => {
  const query = useQuery<
    DragAndDropImageUpload_GetDraftImageProxy,
    DragAndDropImageUpload_GetDraftImageProxyVariables
  >(GET_DRAFT_IMAGE_PROXY, {
    variables: {
      getDraftImageProxyInput: {
        imageId: value ?? '',
        source,
      },
      dynamicImageInput: imageInput,
    },
    skip: !value,
  });

  const { uploadImages } = useImageUpload(source);

  const [isLoading, setIsLoading] = React.useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'image/*',
    noKeyboard: true,
    multiple: false,
    disabled: isDisabled,
    onDrop: async ([file]: File[]): Promise<void> => {
      setIsLoading(true);
      const resource = await uploadImages([file]);
      setIsLoading(false);
      onChange && onChange(resource[0]);
    },
  });

  return (
    <Stack alignItems="center" spacing="4">
      {query.data?.getDraftImageProxy?.imageProxy ? (
        <Box>
          <Image
            src={query.data.getDraftImageProxy.imageProxy}
            alt=""
            fallback={<ImageFallback borderRadius="lg" bg="gray.200" p="6" />}
            {...imageProps}
          />
        </Box>
      ) : nonDraftImageProxy ? (
        <Box>
          <Image
            src={nonDraftImageProxy}
            alt=""
            fallback={<ImageFallback borderRadius="lg" bg="gray.200" p="6" />}
            {...imageProps}
          />
        </Box>
      ) : null}
      <DragAndDropImageUploader
        isDragActive={isDragActive}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isLoading={isLoading}
        isDisabled={isDisabled}
        p="4"
        w="full"
        borderRadius="lg"
        bg={newView ? "secondary.500" : "gray.50"}
        newView={newView}
        imageSizeSmall={imageSizeSmall}
        imageSizeLarge={imageSizeLarge}
      />
    </Stack>
  );
};
